import { ART, ART_BY_KEY, type ArtItem } from './manifest'

/**
 * โหลดไฟล์อาร์ตแบบทนไฟล์หาย
 *
 * หลักการเดียวที่สำคัญ: **ไฟล์ไหนยังไม่มี เกมต้องเล่นได้เหมือนเดิม**
 * ถ้าโหลดไม่ได้จะจำว่า "ไม่มี" แล้วปล่อยให้ฝั่งวาดใช้ของเดิมที่วาดจาก canvas
 * ทำแบบนี้เพื่อให้ทยอยใส่อาร์ตทีละกลุ่มได้ ไม่ต้องรอครบร้อยชิ้นถึงจะเปิดเกมได้
 * และถ้าวันไหนไฟล์บนเซิร์ฟเวอร์หาย เกมก็แค่หน้าตาเดิม ไม่ใช่จอขาว
 */

type Entry = { img: HTMLImageElement; ok: true } | { ok: false }

const cache = new Map<string, Entry>()
let loadedCount = 0
let missingCount = 0

export interface LoadReport {
  loaded: number
  missing: number
  missingKeys: string[]
  ms: number
}

/**
 * โหลดหนึ่งชิ้น โดยลอง .webp ก่อนแล้วค่อยถอยไป .png
 *
 * ไฟล์ .webp มาจาก scripts/optimize-art.mjs ซึ่งเล็กกว่าต้นฉบับราว 10 เท่า
 * แต่ไม่บังคับว่าต้องมี — ถ้าใครเพิ่มอาร์ตใหม่แล้วยังไม่ได้แปลง เกมก็ใช้ .png ได้เลย
 * จึงตรวจจากการโหลดจริง ไม่ได้เก็บธงไว้ในรายการไฟล์ที่ต้องมาคอยแก้ให้ตรงกัน
 *
 * ต้นฉบับ .png ยังอยู่ครบเสมอ ถ้าอยากเลิกใช้ webp ก็ลบไฟล์ .webp ทิ้งได้ทันที
 */
function loadOne(item: ArtItem): Promise<boolean> {
  const webp = item.path.replace(/\.png$/i, '.webp')
  const tryLoad = (src: string) =>
    new Promise<HTMLImageElement | null>((resolve) => {
      const img = new Image()
      // ไฟล์อยู่ origin เดียวกัน แต่ตั้งไว้เผื่อวันหน้าย้ายไป CDN
      img.crossOrigin = 'anonymous'
      img.decoding = 'async'
      img.onload = () => resolve(img)
      img.onerror = () => resolve(null)
      img.src = src
    })

  return tryLoad(webp)
    .then((img) => img ?? tryLoad(item.path))
    .then((img) => {
      if (img) {
        cache.set(item.key, { img, ok: true })
        loadedCount++
        return true
      }
      cache.set(item.key, { ok: false })
      missingCount++
      return false
    })
}

/**
 * หยั่งดูก่อนว่าอาร์ตชนิดนี้มีอยู่ไหม โดยลองโหลดแค่ไฟล์เดียว
 *
 * ถ้าไม่หยั่งก่อน ตอนที่ยังไม่มีอาร์ตเลย เบราว์เซอร์จะยิง 103 request ที่ 404
 * ทุกครั้งที่เปิดเกม เปลืองแบนด์วิดท์และทำให้แท็บ Network อ่านไม่รู้เรื่อง
 * หยั่งชนิดละไฟล์เหลือ 7 request แล้วข้ามทั้งชนิดที่ไม่มี
 */
const kindProbe = new Map<string, Promise<boolean>>()

function probeKind(kind: string): Promise<boolean> {
  let p = kindProbe.get(kind)
  if (!p) {
    const first = ART.find((a) => a.kind === kind)
    p = first ? loadOne(first) : Promise.resolve(false)
    kindProbe.set(kind, p)
  }
  return p
}

/**
 * โหลดอาร์ตกลุ่มหนึ่ง
 * @param tier  'first' = ที่ต้องมีก่อนเข้าเกม · 'later' = ทยอยโหลดตามหลัง
 */
export async function loadArt(tier: ArtItem['tier'], onProgress?: (done: number, total: number) => void): Promise<LoadReport> {
  const t0 = performance.now()
  const kinds = [...new Set(ART.filter((a) => a.tier === tier).map((a) => a.kind))]
  const alive = new Set<string>()
  await Promise.all(kinds.map((k) => probeKind(k).then((ok) => ok && alive.add(k))))

  const all = ART.filter((a) => a.tier === tier)

  // ชนิดที่หยั่งแล้วไม่เจอ ทำเครื่องหมายว่าไม่มีไปเลย จะได้ไม่ยิง request ซ้ำ
  // และตัวเลขสรุปตรงกับความจริง (ไม่ใช่ค้างที่ "ยังไม่ได้ลอง")
  all.forEach((a) => {
    if (!alive.has(a.kind) && !cache.has(a.key)) {
      cache.set(a.key, { ok: false })
      missingCount++
    }
  })

  const list = all.filter((a) => alive.has(a.kind) && !cache.has(a.key))
  let done = 0
  await Promise.all(
    list.map((item) =>
      loadOne(item).then(() => {
        done++
        onProgress?.(done, list.length)
      }),
    ),
  )

  const missingKeys = all.filter((a) => !cache.get(a.key)?.ok).map((a) => a.key)
  return {
    loaded: all.length - missingKeys.length,
    missing: missingKeys.length,
    missingKeys,
    ms: Math.round(performance.now() - t0),
  }
}

/** โหลดกลุ่ม later แบบไม่บล็อก ใช้ตอนเกมเริ่มแล้ว */
export function loadArtLater(): void {
  void loadArt('later')
}

/** รูปที่โหลดแล้ว หรือ null ถ้าไม่มี — ฝั่งวาดต้องรับ null ได้เสมอ */
export function artImage(key: string): HTMLImageElement | null {
  const e = cache.get(key)
  return e?.ok ? e.img : null
}

/** มีอาร์ตชิ้นนี้ไหม (ไม่สนว่าโหลดเสร็จหรือยัง) */
export function hasArt(key: string): boolean {
  return cache.get(key)?.ok === true
}

/**
 * พาธของอาร์ตชิ้นหนึ่ง สำหรับฝั่ง React ที่ต้องใส่ใน <img src>
 * (ฝั่ง 3 มิติใช้ artImage ที่คืน HTMLImageElement ไปอบเป็นเท็กซ์เจอร์แทน)
 * คืน null ถ้าไม่รู้จักคีย์ ผู้เรียกต้องมีทางสำรองเสมอ
 */
export function artPath(key: string): string | null {
  return ART_BY_KEY.get(key)?.path ?? null
}

/**
 * รูปตัวละครตามท่า ถ้าท่านั้นยังไม่มีจะถอยไปใช้ท่า idle ให้อัตโนมัติ
 * ทำให้สั่งอาร์ตแค่บางท่าก่อนได้ ตามที่เขียนไว้ในสเปก
 */
export function charImage(id: string, pose: string): HTMLImageElement | null {
  return artImage(`char:${id}:${pose}`) ?? artImage(`char:${id}:idle`)
}

/** สรุปสถานะไว้ดูตอนไล่ปัญหา เรียกได้ที่ milloArt() ในคอนโซล */
export function artStatus() {
  const byKind: Record<string, { มี: number; ขาด: number }> = {}
  ART.forEach((a) => {
    const b = (byKind[a.kind] ??= { มี: 0, ขาด: 0 })
    const e = cache.get(a.key)
    if (!e) return
    if (e.ok) b.มี++
    else b.ขาด++
  })
  return {
    รวมในรายการ: ART.length,
    โหลดสำเร็จ: loadedCount,
    ไม่มีไฟล์: missingCount,
    ยังไม่ได้ลอง: ART.length - loadedCount - missingCount,
    ตามชนิด: byKind,
    ขาดตัวอย่าง: ART.filter((a) => cache.get(a.key)?.ok === false)
      .slice(0, 8)
      .map((a) => a.path),
  }
}

;(window as unknown as { milloArt: typeof artStatus }).milloArt = artStatus

/** ใช้ตอนทดสอบ: ล้างแคชเพื่อโหลดใหม่ */
export function resetArt(): void {
  cache.clear()
  loadedCount = 0
  missingCount = 0
}

export { ART_BY_KEY }
